import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, Scan, AlertCircle, CheckCircle, Loader } from 'lucide-react';

const UPCScanner = ({
  onScanComplete,
  onClose,
  onOpenMovieSearch,
  supabaseUrl,
  supabaseAnonKey,
}) => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('prompt');
  const [zxingLoaded, setZxingLoaded] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualUPC, setManualUPC] = useState('');
  const [failedUPC, setFailedUPC] = useState(null);
  const [showManualSearch, setShowManualSearch] = useState(false);
  const [scanAttempts, setScanAttempts] = useState(0);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);
  const scanningRef = useRef(false);

  // Detect if user is on desktop vs mobile
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 1);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 40; // 20 seconds max wait time for mobile

    const checkZXingLoaded = () => {
      attempts++;
      console.log(`🔍 Checking ZXing library... attempt ${attempts}/${maxAttempts}`);

      if (window.ZXing) {
        console.log('✅ ZXing library loaded successfully!');
        console.log('📦 ZXing object:', Object.keys(window.ZXing));
        setZxingLoaded(true);
        setError('');
      } else if (attempts >= maxAttempts) {
        console.error('❌ ZXing library failed to load after', maxAttempts, 'attempts');
        setError('⚠️ Barcode scanner failed to load. Please refresh the page and try again.');
        setShowManualEntry(true);
      } else {
        console.log('⏳ ZXing not ready, retrying...');
        setTimeout(checkZXingLoaded, 500);
      }
    };

    checkZXingLoaded();

    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    if (!window.ZXing) {
      console.error('❌ ZXing library not loaded');
      setError('Barcode scanner not available. Please refresh the page.');
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('❌ Camera API not supported');
      setError('Camera API not supported in this browser');
      setShowManualEntry(true);
      return;
    }

    console.log('📱 Camera API available');

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'camera' });
      console.log('🔒 Camera permission status:', permissionStatus.state);

      if (permissionStatus.state === 'denied') {
        console.error('❌ Camera permission denied');
        setError('Camera access blocked. Please enable camera permission in browser settings.');
        setCameraPermission('denied');
        setShowManualEntry(true);
        return;
      }
    } catch (permError) {
      console.warn('⚠️ Permission check failed:', permError.message);
      // Permission check failed, continue anyway
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      const audioDevices = devices.filter(device => device.kind === 'audioinput');

      console.log(
        '🎥 All devices:',
        devices.map(d => `${d.kind}: ${d.label || 'unlabeled'}`)
      );
      console.log('📹 Video devices:', videoDevices.length);
      console.log('🎤 Audio devices:', audioDevices.length);

      if (videoDevices.length === 0) {
        console.error('❌ No video devices found');
        setError(
          'No camera detected. Check: 1) macOS System Preferences → Security & Privacy → Camera 2) Browser permissions (camera icon in address bar)'
        );
        setShowManualEntry(true);
        return;
      }

      // Check if devices have labels (indicates permission granted)
      const hasLabels = videoDevices.some(device => device.label);
      if (!hasLabels) {
        console.warn('⚠️ Video devices found but no labels - permissions may be needed');
        setError('Camera found but permission needed. Please allow camera access when prompted.');
      }
    } catch (enumError) {
      console.error('❌ Device enumeration failed:', enumError.message);
      setError(
        'Camera access error: ' + enumError.message + '. Check browser and macOS permissions.'
      );
      setShowManualEntry(true);
      return;
    }

    try {
      setError('⏳ Starting camera...');
      setIsScanning(true);
      scanningRef.current = true;
      setScanAttempts(0); // Reset scan counter

      // Mobile-first camera access - prioritize back camera for barcode scanning
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'environment', // Back camera for barcode scanning
            width: { ideal: 1920, min: 1280 }, // Higher res for better barcode detection
            height: { ideal: 1080, min: 720 },
          },
        });
      } catch (cameraError) {
        // Simple fallback for any camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' }, // Still prefer back camera
        });
      }

      streamRef.current = stream;
      setCameraPermission('granted');

      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        try {
          await videoRef.current.play();

          // Mobile-first: Brief delay to let video stream stabilize
          setTimeout(() => {
            if (scanningRef.current && videoRef.current) {
              initializeZXing();
            }
          }, 1000);
        } catch (videoError) {
          setError('Failed to start video: ' + videoError.message);
          setIsScanning(false);
          scanningRef.current = false;
        }
      } else {
        setError('Video element not found');
        setIsScanning(false);
        scanningRef.current = false;
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setCameraPermission('denied');
        setError('Camera permission denied. Please allow camera access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device');
        setShowManualEntry(true);
      } else {
        setError('Failed to access camera: ' + err.message);
        setShowManualEntry(true);
      }
      setIsScanning(false);
      scanningRef.current = false;
    }
  };

  const initializeZXing = () => {
    if (!window.ZXing) {
      setError('Barcode scanner library not loaded. Please refresh the page.');
      setIsScanning(false);
      scanningRef.current = false;
      return;
    }

    if (!videoRef.current) {
      setError('Video element not ready. Please try again.');
      setIsScanning(false);
      scanningRef.current = false;
      return;
    }

    try {
      const codeReader = new window.ZXing.BrowserMultiFormatReader();
      readerRef.current = codeReader;

      setError('🔍 Scanner ready - point camera at barcode');

      codeReader
        .decodeFromVideoDevice(undefined, videoRef.current, (result, err) => {
          if (!scanningRef.current) return;

          console.log('🔍 ZXing scan attempt...', { result: !!result, error: !!err });
          setScanAttempts(prev => prev + 1);

          if (result) {
            const code = result.getText();
            const format = result.getBarcodeFormat();

            console.log('✅ Barcode detected!', { code, format, length: code.length });
            setError(`📱 Detected: ${code} (${format})`);

            // Accept 12-digit UPC codes
            if (code && /^\d{12}$/.test(code)) {
              console.log('✅ Valid 12-digit UPC, processing...');
              setScanResult(code);
              handleScanSuccess(code);
            }
            // Also accept 13-digit EAN codes (common on VHS)
            else if (code && /^\d{13}$/.test(code)) {
              console.log('✅ Valid 13-digit EAN, processing...');
              setScanResult(code);
              handleScanSuccess(code);
            }
            // Accept other numeric codes for testing
            else if (code && /^\d+$/.test(code) && code.length >= 8) {
              console.log('✅ Valid numeric barcode, processing...');
              setScanResult(code);
              handleScanSuccess(code);
            } else {
              console.warn('❌ Invalid barcode format:', { code, length: code.length, format });
              setError(`❌ Invalid barcode: ${code} (${code.length} digits, need 12-13)`);
            }
          }

          if (err && !(err instanceof window.ZXing.NotFoundException)) {
            console.warn('⚠️ Scanner error:', err.message);
            setError('Scanner error: ' + err.message);
          } else if (err instanceof window.ZXing.NotFoundException) {
            // This is normal - just means no barcode found in current frame
            console.log('🔍 No barcode found in frame (normal)');
          }
        })
        .catch(decodeError => {
          setError('Failed to start scanner: ' + decodeError.message);
          setIsScanning(false);
          scanningRef.current = false;
        });
    } catch (initError) {
      setError('Failed to start barcode scanner: ' + initError.message);
      setIsScanning(false);
      scanningRef.current = false;
    }
  };

  const stopScanning = () => {
    setIsScanning(false);
    scanningRef.current = false;
    setError('');

    try {
      if (readerRef.current) {
        readerRef.current.reset();
        readerRef.current = null;
      }
    } catch (err) {
      // Silent error handling
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleScanSuccess = async upc => {
    setIsProcessing(true);
    stopScanning();

    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/process-upc-2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ upc }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        onScanComplete(result);
      } else {
        // Handle different types of errors
        if (result.allowManualSearch) {
          setFailedUPC(upc);
          setShowManualSearch(true);
          setError(`UPC ${upc} not found in database. VHS tapes often aren't in UPC databases.`);
        } else {
          setError(result.error || 'UPC lookup failed');
        }
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('UPC lookup error:', err);
      setError('Failed to lookup movie data for UPC: ' + upc + ' - ' + err.message);
      setIsProcessing(false);
    }
  };

  const handleManualEntry = () => {
    setShowManualEntry(!showManualEntry);
    setManualUPC('');
    setError('');
  };

  const handleManualSubmit = () => {
    if (manualUPC && /^\d{12}$/.test(manualUPC)) {
      handleScanSuccess(manualUPC);
    } else {
      setError('Please enter a valid 12-digit UPC code');
    }
  };

  const openMovieSearch = () => {
    if (onOpenMovieSearch) {
      onOpenMovieSearch();
    } else {
      // Fallback: just close UPC scanner
      onClose();
    }
  };

  const testCameraAccess = async () => {
    console.log('🧪 Testing basic camera access...');

    // First check if MediaDevices API is available
    if (!navigator.mediaDevices) {
      setError('❌ MediaDevices API not available');
      return;
    }

    try {
      // First enumerate devices to see what's available
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      console.log('🔍 Device enumeration test:');
      console.log('  📹 Video devices found:', videoDevices.length);
      console.log(
        '  🎥 All devices:',
        devices.map(d => `${d.kind}: ${d.label || 'unlabeled'}`)
      );

      if (videoDevices.length === 0) {
        setError(
          '❌ No video input devices found. Check System Preferences → Security & Privacy → Camera'
        );
        return;
      }

      // Try to get user media
      console.log('🚀 Requesting camera stream...');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      console.log('✅ Camera stream obtained successfully!');
      console.log('📹 Stream tracks:', stream.getTracks().length);

      stream.getTracks().forEach(track => {
        console.log(`📺 Track: ${track.kind}, label: ${track.label}, enabled: ${track.enabled}`);
        track.stop();
      });

      setError('✅ Camera test passed! Your camera is working. Try scanning now.');
    } catch (err) {
      console.error('❌ Camera test failed:', err);
      console.log('📝 Error details:', {
        name: err.name,
        message: err.message,
        constraint: err.constraint,
      });

      let errorMsg = '❌ Camera test failed: ';
      if (err.name === 'NotAllowedError') {
        errorMsg += 'Permission denied. Allow camera access in browser settings.';
      } else if (err.name === 'NotFoundError') {
        errorMsg += 'No camera found. Check System Preferences.';
      } else if (err.name === 'NotReadableError') {
        errorMsg += 'Camera in use by another app.';
      } else {
        errorMsg += err.message;
      }

      setError(errorMsg);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-black border border-gray-600 rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b border-gray-600">
          <h3 className="text-lg font-semibold text-white">Scan UPC Barcode</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-red-500 p-1 rounded-full">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Camera View */}
          {isScanning && (
            <div className="relative">
              <video
                ref={videoRef}
                className="w-full h-64 bg-black rounded-lg object-cover"
                autoPlay
                playsInline
                muted
              />

              {/* Scanning overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="border-2 border-red-500 w-72 h-24 rounded-lg opacity-75">
                  <div className="w-full h-full border border-white rounded-lg animate-pulse"></div>
                </div>
              </div>

              <div className="absolute bottom-2 left-2 right-2 text-center">
                <p className="text-white text-sm bg-black bg-opacity-70 rounded px-3 py-2">
                  Position barcode in the red frame
                </p>
              </div>

              <div className="absolute top-2 left-2 right-2">
                <div className="bg-black bg-opacity-70 text-white text-xs rounded px-2 py-1 text-center">
                  Hold phone steady • Good lighting • Focus on barcode
                </div>
              </div>
            </div>
          )}

          {/* Processing State */}
          {isProcessing && (
            <div className="text-center py-8">
              <Loader className="animate-spin mx-auto mb-2 text-red-500" size={32} />
              <p className="text-gray-300">Looking up movie information...</p>
              {scanResult && <p className="text-sm text-gray-400 mt-1">UPC: {scanResult}</p>}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-900 border border-red-500 rounded-lg p-3 flex items-start space-x-2">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
              <div className="flex-1">
                <p className="text-red-300 text-sm">{error}</p>
                {showManualSearch && failedUPC && (
                  <div className="mt-3 space-y-2">
                    <p className="text-gray-300 text-xs">What would you like to do?</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={openMovieSearch}
                        className="bg-blue-600 text-white text-xs py-2 px-3 rounded hover:bg-blue-700"
                      >
                        Search Movies
                      </button>
                      <button
                        onClick={() => {
                          setManualUPC('');
                          setShowManualEntry(true);
                          setShowManualSearch(false);
                        }}
                        className="bg-gray-600 text-white text-xs py-2 px-3 rounded hover:bg-gray-700"
                      >
                        Try Another UPC
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Success Display */}
          {scanResult && !isProcessing && !error && (
            <div className="bg-green-900 border border-green-500 rounded-lg p-3 flex items-center space-x-2">
              <CheckCircle className="text-green-500" size={16} />
              <p className="text-green-300 text-sm">Scanned: {scanResult}</p>
            </div>
          )}

          {/* Controls */}
          <div className="space-y-3">
            {!isScanning && !isProcessing && zxingLoaded && (
              <>
                <div className="text-center mb-3">
                  <p className="text-gray-400 text-xs">
                    {isMobile
                      ? 'Use your phone camera to scan VHS barcodes'
                      : 'Camera scanning requires a mobile device with a camera'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={startScanning}
                    disabled={!isMobile}
                    className={`flex items-center justify-center space-x-1 py-2 px-3 rounded-lg text-sm ${
                      isMobile
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    <Camera size={16} />
                    <span>{isMobile ? 'Scan' : 'Mobile Only'}</span>
                  </button>

                  <button
                    onClick={handleManualEntry}
                    className="flex items-center justify-center space-x-1 bg-black border border-gray-600 text-white py-2 px-3 rounded-lg hover:border-red-500 text-sm"
                  >
                    <Scan size={14} />
                    <span>Manual</span>
                  </button>
                </div>
                <div className="mt-2 text-center">
                  <button
                    onClick={testCameraAccess}
                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                  >
                    Debug Camera
                  </button>
                </div>
              </>
            )}

            {isScanning && (
              <button
                onClick={stopScanning}
                className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700"
              >
                Stop Scanning
              </button>
            )}

            {!zxingLoaded && !error && (
              <div className="text-center py-4">
                <Loader className="animate-spin mx-auto mb-2 text-gray-400" size={24} />
                <p className="text-gray-300 text-sm mb-2">Loading barcode scanner...</p>
                <p className="text-gray-400 text-xs mb-3">This may take a few seconds</p>
              </div>
            )}

            {!zxingLoaded && error && (
              <div className="text-center py-4">
                <p className="text-gray-400 text-xs mb-3">
                  Scanner unavailable - use manual entry:
                </p>
                <div className="space-y-2">
                  <button
                    onClick={testCameraAccess}
                    className="flex items-center justify-center space-x-1 bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 text-sm mx-auto"
                  >
                    <Camera size={14} />
                    <span>Test Camera</span>
                  </button>
                  <button
                    onClick={handleManualEntry}
                    className="flex items-center justify-center space-x-1 bg-black border border-gray-600 text-white py-2 px-3 rounded-lg hover:border-red-500 text-sm mx-auto"
                  >
                    <Scan size={14} />
                    <span>Manual Entry</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Manual UPC Entry */}
          {showManualEntry && (
            <div className="bg-black border border-gray-600 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">Enter UPC Manually</h4>
              <div className="space-y-3">
                <input
                  type="text"
                  value={manualUPC}
                  onChange={e => setManualUPC(e.target.value.replace(/\D/g, '').slice(0, 12))}
                  placeholder="Enter 12-digit UPC code"
                  className="w-full px-3 py-2 border border-gray-600 bg-black text-white rounded-lg hover:border-red-500 focus:ring-0 focus:border-red-500 focus:outline-none"
                  maxLength={12}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleManualSubmit}
                    disabled={manualUPC.length !== 12}
                    className="flex-1 bg-red-600 text-white py-2 px-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Submit
                  </button>
                  <button
                    onClick={() => setShowManualEntry(false)}
                    className="flex-1 bg-black border border-gray-600 text-white py-2 px-3 rounded-lg hover:border-red-500 text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Camera Permission Help */}
          {cameraPermission === 'denied' && (
            <div className="bg-yellow-900 border border-yellow-500 rounded-lg p-3">
              <p className="text-yellow-300 text-sm">
                <strong>Camera access denied:</strong> Please allow camera permission in your
                browser settings and refresh the page.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UPCScanner;
